import type { RunTestsResult, TestRunUiStatus } from '../types';
import { RunTestsFileRow } from './RunTestsFileRow';

function statusOf(t: NonNullable<RunTestsResult['fileResults'][number]['tests']>[number]): TestRunUiStatus {
  return (t.status as TestRunUiStatus | undefined) ?? (t.passed ? 'passed' : 'failed');
}

export function RunTestsResultsList({ results }: { results: RunTestsResult }) {
  // Roll-up counters used for the panel header. Recompute from the
  // file-level data instead of trusting the top-level totals so live
  // streaming snapshots stay in sync without an extra reducer pass.
  let total = 0;
  let passed = 0;
  let failed = 0;
  let running = 0;
  for (const fr of results.fileResults) {
    for (const t of fr.tests ?? []) {
      total++;
      const s = statusOf(t);
      if (s === 'passed') passed++;
      else if (s === 'failed' || s === 'error') failed++;
      else if (s === 'running' || s === 'pending') running++;
    }
  }
  const isLive = running > 0;
  const hasFailures = failed > 0;

  return (
    <div className="pipeline-test-results" data-testid="pipeline-test-results">
      {(total > 0 || results.summary) && (
        <div
          className={`pipeline-test-summary-header${hasFailures ? ' failed' : ''}${isLive ? ' running' : ''}`}
        >
          {hasFailures ? (
            <span>
              <strong>{failed}</strong> failed · {passed}/{total} passed
              {isLive && ` · ${running} running`}
            </span>
          ) : isLive ? (
            <span>
              Running tests… {passed}/{total} passed
              {failed > 0 && ` · ${failed} failed`}
            </span>
          ) : total > 0 ? (
            <span>
              <strong>{passed}/{total}</strong> tests passed
            </span>
          ) : (
            <span>{results.summary}</span>
          )}
        </div>
      )}
      {results.fileResults.map((file, i) => (
        <RunTestsFileRow key={`${file.path}-${i}`} file={file} />
      ))}
      {results.summary && total > 0 && (
        <div className="pipeline-test-summary">{results.summary}</div>
      )}
    </div>
  );
}
